import { FlexRow } from '@minecraft.atoms';
import React, { FC } from 'react';
import { gqlSocialMedia, gqlSocialMediaOverview, gqlSocialMediaPlatform } from '@minecraft.graphql-types';
import { useTranslation } from '@blocs.i18n';
import { formatMetricCount } from 'minecraft/src/components/utils/helpers/helpers';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import { SocialMediaButton } from '../SocialMediaButton';

export type SocialMediaSectionProps = {
  socialMedia: gqlSocialMedia;
  id: string;
};

type SocialPlatformButtonProps = {
  platform: gqlSocialMediaPlatform;
  overview?: gqlSocialMediaOverview;
  id: string;
};

const SOCIAL_MEDIA_MAP = {
  [gqlSocialMediaPlatform.INSTAGRAM]: {
    platformName: 'Instagram',
    iconName: 'profile-social-instagram',
    heapEventName: HEAP_EVENT_NAMES.CD_INSTAGRAM_CLICK_BASEBALL_CARD,
  },
  [gqlSocialMediaPlatform.YOUTUBE]: {
    platformName: 'YouTube',
    iconName: 'profile-social-youtube',
    heapEventName: HEAP_EVENT_NAMES.CD_YOUTUBE_CLICK_BASEBALL_CARD,
  },
  [gqlSocialMediaPlatform.TIKTOK]: {
    platformName: 'TikTok',
    iconName: 'profile-social-tiktok',
    heapEventName: HEAP_EVENT_NAMES.CD_TIKTOK_CLICK_BASEBALL_CARD,
  },
};

const SocialPlatformButton: FC<SocialPlatformButtonProps> = ({ platform, overview, id }) => {
  const isLinked = Boolean(overview);
  const { t } = useTranslation();

  const buttonLabel = isLinked ? formatMetricCount(overview?.metrics?.followerCount) : 'N/A';
  const { iconName, heapEventName, platformName } = SOCIAL_MEDIA_MAP[platform];

  const buttonAriaLabel = isLinked
    ? t('common:socialLinking.button.linked.ariaLabel', {
        socialMediaPlatform: platformName,
      })
    : t('common:socialLinking.button.notLinked.ariaLabel', {
        socialMediaPlatform: platformName,
      });

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isLinked && overview?.link) {
      captureHeapEvent({
        name: heapEventName,
      });
      window.open(overview.link, '_blank', 'noopener noreferrer');
    }
  };

  return (
    <SocialMediaButton
      id={`${platform}-${id}`}
      label={buttonLabel}
      disabled={!isLinked}
      showTooltip={!isLinked}
      aria-label={buttonAriaLabel}
      wrapperClassName="cn_atom_py-2"
      iconName={iconName}
      onClick={handleClick}
      tooltipContent={t('common:socialLinking.notLinked')}
    />
  );
};

export const SocialMediaSection: FC<SocialMediaSectionProps> = ({ socialMedia, id }) => {
  return (
    <FlexRow alignItems="center" gap="1" wrap="nowrap" data-testid="social-media-section" my="2">
      <SocialPlatformButton platform={gqlSocialMediaPlatform.INSTAGRAM} overview={socialMedia?.instagram} id={id} />
      <SocialPlatformButton platform={gqlSocialMediaPlatform.YOUTUBE} overview={socialMedia?.youtube} id={id} />
      <SocialPlatformButton platform={gqlSocialMediaPlatform.TIKTOK} overview={socialMedia?.tiktok} id={id} />
    </FlexRow>
  );
};
